import axios from 'axios';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // Check for explicit environment variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Use production URL in production build
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.shopnonagar.com/api';
  }
  
  // Default to localhost for development
  return 'http://localhost:8000/api';
};

// Get base URL without /api suffix (for images and static files)
export const getBaseUrl = () => {
  // Check for explicit environment variable first
  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }
  
  // In production, try to use the current window origin or derive from API URL
  if (process.env.NODE_ENV === 'production') {
    // If we're in a browser, use the current origin (same domain)
    if (typeof window !== 'undefined' && window.location.origin) {
      return window.location.origin;
    }
    // Fallback: derive from API URL by removing /api
    const apiUrl = getApiBaseUrl();
    return apiUrl.replace(/\/api$/, '');
  }
  
  // Default to localhost for development
  return 'http://localhost:8000';
};

// Helper function to get full URL for images/paths
export const getImageUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  
  // If it's already a full URL, check if it's localhost and needs to be replaced
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // If it contains localhost, extract the path and rebuild with correct base URL
    if (path.includes('localhost')) {
      try {
        const url = new URL(path);
        const pathOnly = url.pathname;
        const baseUrl = getBaseUrl();
        return `${baseUrl}${pathOnly}`;
      } catch (e) {
        // If URL parsing fails, try to extract path manually
        const pathMatch = path.match(/\/storage\/.*/);
        if (pathMatch) {
          const baseUrl = getBaseUrl();
          return `${baseUrl}${pathMatch[0]}`;
        }
      }
    }
    // If it's a valid non-localhost URL, return as is
    return path;
  }
  
  // Otherwise, prepend base URL
  const baseUrl = getBaseUrl();
  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token and set content type
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const language = localStorage.getItem('app-language') || 'en';
    config.headers['X-Locale'] = language;
    config.headers['Accept-Language'] = language;
    
    // Set Content-Type for JSON requests, but let FormData handle its own Content-Type
    if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isPublicRoute = ['/', '/login', '/register', '/notices'].includes(currentPath);
      
      // Only redirect to login if on a protected route
      if (!isPublicRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        // For public routes, just clear the invalid token silently
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    return Promise.reject(error);
  }
);

export default api;
